import React, { useCallback, useEffect, useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { ProjectsAPI, VariationSetsAPI, AssessmentsAPI, BillingAPI } from "@/lib/api";
import NewAssessment from "@/pages/NewAssessment";
import NewVariationSet from "@/pages/NewVariationSet";
import Library from "@/components/Library";
import Collaborators from "@/components/Collaborators";
import BuyCreditsModal from "@/components/BuyCreditsModal";
import { useAuth } from "@/lib/providers";
import { toast } from "sonner";

const KIND_LABELS = { sub_premise: "Sub-premise", system_level: "System level", primary: "Primary" };
const assessmentKindLabel = (a) => KIND_LABELS[a?.assessment_kind] || KIND_LABELS.primary;

function renderScore(a) {
  if (a.status === "complete") {
    return (
      <>
        <div className="v-score" style={{ fontSize: 48, color: "var(--v-accent)" }}>{Number(a.overall_score || 0).toFixed(1)}</div>
        <div className="mono" style={{ color: "var(--v-text-2)", fontSize: 11 }}>/10</div>
      </>
    );
  }
  if (a.status === "failed") {
    return <div className="overline" style={{ color: "var(--v-accent)" }}>Failed</div>;
  }
  return <div className="overline">Processing…</div>;
}

export default function ProjectDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const [project, setProject] = useState(null);
  const [assessments, setAssessments] = useState([]);
  const [variationSets, setVariationSets] = useState([]);
  const [tab, setTab] = useState("timeline");
  const [showNew, setShowNew] = useState(false);
  const [showNewVariation, setShowNewVariation] = useState(false);
  const [kind, setKind] = useState("primary");
  const [duplicateSeed, setDuplicateSeed] = useState(null);
  // Paywall gate — shown when an external (paying) user with 0 credits tries
  // to kick off any new audit flow. Internal users bypass this entirely.
  const [showPaywall, setShowPaywall] = useState(false);
  const [walletCredits, setWalletCredits] = useState(null);
  const isExternal = user?.user_type === "external";

  // Lazy wallet-balance check used by every "+ New …" button. We don't
  // pre-fetch on page load so internal designers never pay the extra call —
  // we only hit /billing/wallet when a paying user is actually about to kick
  // off a new audit. On an error we fail OPEN (let the backend 402 catch it)
  // rather than block legitimate users on a transient network blip.
  const gateOrOpen = async (onPass) => {
    if (!isExternal) { onPass(); return; }
    try {
      const { data } = await BillingAPI.wallet();
      const credits = Number(data?.wallet?.audit_credits || 0);
      setWalletCredits(credits);
      if (credits < 1) {
        setShowPaywall(true);
        return;
      }
    } catch (err) {
      // Network / 5xx: let backend be the final arbiter. Still log in dev
      // so a broken gate is debuggable.
      if (process.env.NODE_ENV !== "production") {
        console.warn("gateOrOpen wallet fetch failed:", err?.message || err);
      }
    }
    onPass();
  };

  const load = useCallback(async () => {
    const [p, a, vs] = await Promise.all([
      ProjectsAPI.get(id),
      ProjectsAPI.assessments(id),
      VariationSetsAPI.list(id),
    ]);
    setProject(p.data);
    setAssessments(a.data);
    setVariationSets(vs.data || []);
  }, [id]);

  const toggleArchive = async () => {
    if (!project) return;
    const next = project.status === "archived" ? "live" : "archived";
    await ProjectsAPI.patch(id, { status: next });
    setProject({ ...project, status: next });
  };

  useEffect(() => { load(); }, [load]);

  // Duplicate-assessment flow: when `?duplicate=asmt_XXX` is present in the URL,
  // fetch that assessment (with pack/competitor/existing images) and open the
  // New Assessment modal pre-filled from it. Clears the query string after so
  // a page refresh doesn't re-trigger the fetch.
  useEffect(() => {
    const dupId = searchParams.get("duplicate");
    if (!dupId || !project) return;
    let cancelled = false;
    (async () => {
      try {
        const { data } = await AssessmentsAPI.get(dupId);
        if (cancelled) return;
        if (data.project_id !== project.project_id) {
          toast.error("That assessment doesn't belong to this project.");
        } else {
          setDuplicateSeed(data);
          setKind(data.assessment_kind || "primary");
          setShowNew(true);
        }
      } catch {
        toast.error("Couldn't load the assessment to duplicate.");
      } finally {
        // Remove the ?duplicate param so a refresh doesn't re-open the modal
        const nextParams = new URLSearchParams(searchParams);
        nextParams.delete("duplicate");
        setSearchParams(nextParams, { replace: true });
      }
    })();
    return () => { cancelled = true; };
  }, [project, searchParams, setSearchParams]);

  if (!project) return <div className="v-container" style={{ paddingTop: 80 }}><div className="overline">Loading…</div></div>;

  return (
    <div className="v-container" style={{ paddingTop: 48, paddingBottom: 96 }}>
      <Link to="/studio" className="overline" data-testid="back-to-studio">← Studio</Link>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-end", flexWrap: "wrap", gap: 24, marginTop: 16 }}>
        <div>
          <div className="overline">
            {project.category} · {project.project_type === "packaging_refresh" ? "Packaging refresh" : "New development"}
            {project.status === "archived" ? " · Archived" : ""}
          </div>
          <h1 className="display" style={{ fontSize: "clamp(40px,5vw,72px)", lineHeight: 1, marginTop: 10 }}>{project.brand_name}</h1>
          {project.client_name && <div className="mono" style={{ color: "var(--v-text-2)", marginTop: 6 }}>Client · {project.client_name}</div>}
        </div>
        <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
          <button className="v-btn" data-testid="run-primary-assessment-btn" onClick={() => gateOrOpen(() => { setKind("primary"); setShowNew(true); })}>+ New assessment</button>
          <button className="v-btn v-btn-outline" data-testid="run-sub-premise-btn" onClick={() => gateOrOpen(() => { setKind("sub_premise"); setShowNew(true); })}>+ Sub-premise / variant</button>
          <button className="v-btn v-btn-outline" data-testid="run-system-level-btn" onClick={() => gateOrOpen(() => { setKind("system_level"); setShowNew(true); })}>+ System architecture</button>
          <button className="v-btn v-btn-outline" data-testid="run-variation-set-btn" onClick={() => gateOrOpen(() => setShowNewVariation(true))}>+ Compare variations</button>
          <button className="v-btn v-btn-outline" data-testid="archive-toggle-btn" onClick={toggleArchive}>
            {project.status === "archived" ? "Restore project" : "Archive"}
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-12 gap-12" style={{ marginTop: 48 }}>
        <div className="md:col-span-4">
          <div className="v-surface" style={{ padding: 24 }}>
            <div className="overline">Brief</div>
            <KV k="Positioning" v={project.positioning} />
            <KV k="Target audience" v={project.target_audience} />
            <KV k="Aspirations" v={project.aspirations} />
            <KV k="Competitors" v={(project.competitors || []).join(", ") || "—"} />
            {project.notes && <KV k="Notes" v={project.notes} />}
          </div>
          <div style={{ marginTop: 16 }}>
            <Collaborators projectId={id} ownerId={project.owner_id} />
          </div>
        </div>

        <div className="md:col-span-8">
          <div style={{ display: "flex", gap: 16, borderBottom: "1px solid var(--v-border)", marginBottom: 24, flexWrap: "wrap" }}>
            {[
              { id: "timeline", label: "Timeline" },
              { id: "sub_premise", label: "Sub-premises" },
              { id: "system_level", label: "System level" },
              { id: "variations", label: "Variations" },
              { id: "library", label: "Library" },
            ].map((t) => (
              <button
                key={t.id}
                data-testid={`tab-${t.id}`}
                onClick={() => setTab(t.id)}
                style={{
                  background: "transparent", border: "none", padding: "12px 0",
                  fontFamily: "JetBrains Mono, monospace", fontSize: 12, letterSpacing: ".12em", textTransform: "uppercase",
                  color: tab === t.id ? "var(--v-text)" : "var(--v-text-2)",
                  borderBottom: tab === t.id ? "2px solid var(--v-accent)" : "2px solid transparent",
                  marginBottom: -1, cursor: "pointer",
                }}
              >
                {t.label}
              </button>
            ))}
          </div>

          {tab === "library" ? (
            <Library projectId={id} project={project} />
          ) : tab === "variations" ? (
            <VariationsList projectId={id} variationSets={variationSets} onCreate={() => gateOrOpen(() => setShowNewVariation(true))} />
          ) : (
            <>
              {assessments.length === 0 && (
                <div className="v-surface" style={{ padding: 32 }} data-testid="no-assessments">
                  <div className="overline">No assessments yet</div>
                  <p style={{ marginTop: 8, color: "var(--v-text-2)" }}>Upload a pack design to receive Verdict's seven-dimension critique.</p>
                </div>
              )}

              <div className="v-stagger grid grid-cols-1 gap-4">
                {assessments
                  .filter((a) => tab === "timeline" || a.assessment_kind === tab)
                  .map((a) => (
                    <Link key={a.assessment_id} to={`/assessments/${a.assessment_id}`} data-testid={`assessment-row-${a.assessment_id}`} style={{ textDecoration: "none", color: "inherit" }}>
                      <div className="v-surface" style={{ padding: 20, display: "flex", alignItems: "center", justifyContent: "space-between", gap: 24 }}>
                        <div>
                          <div className="overline">
                            {assessmentKindLabel(a)}
                            {a.sub_premise_name ? ` · ${a.sub_premise_name}` : ""}
                            {a.status !== "complete" ? ` · ${a.status}` : ""}
                          </div>
                          <h3 style={{ marginTop: 6, fontSize: 22 }}>{a.title}</h3>
                          <div className="mono" style={{ color: "var(--v-text-2)", fontSize: 12, marginTop: 4 }}>
                            by {a.designer_name} · {new Date(a.created_at).toLocaleString()}
                          </div>
                        </div>
                        <div style={{ textAlign: "right" }}>
                          {renderScore(a)}
                        </div>
                      </div>
                    </Link>
                  ))}
              </div>
            </>
          )}
        </div>
      </div>

      {showNew && (
        <NewAssessment
          project={project}
          kind={kind}
          assessments={assessments}
          duplicateSeed={duplicateSeed}
          onClose={() => { setShowNew(false); setDuplicateSeed(null); }}
          onCreated={(aId) => navigate(`/assessments/${aId}`)}
        />
      )}

      {showNewVariation && (
        <NewVariationSet
          project={project}
          onClose={() => setShowNewVariation(false)}
          onCreated={(vsId) => navigate(`/variation-sets/${vsId}`)}
        />
      )}

      <BuyCreditsModal
        open={showPaywall}
        onClose={() => setShowPaywall(false)}
        creditsAvailable={walletCredits ?? 0}
      />
    </div>
  );
}

function VariationsList({ projectId, variationSets, onCreate }) {
  if (!variationSets.length) {
    return (
      <div className="v-surface" style={{ padding: 32 }} data-testid="no-variation-sets">
        <div className="overline">Compare design variations</div>
        <p style={{ marginTop: 8, color: "var(--v-text-2)", maxWidth: 520 }}>
          Upload 2–4 variations of the same design and Verdict will audit each one, then rank them
          side-by-side across the seven dimensions and recommend the one to ship.
        </p>
        <button className="v-btn" style={{ marginTop: 18 }} onClick={onCreate} data-testid="empty-create-variation-set">
          + Compare variations
        </button>
      </div>
    );
  }
  return (
    <div className="v-stagger grid grid-cols-1 gap-4">
      {variationSets.map((vs) => (
        <Link
          key={vs.variation_set_id}
          to={`/variation-sets/${vs.variation_set_id}`}
          data-testid={`variation-set-row-${vs.variation_set_id}`}
          style={{ textDecoration: "none", color: "inherit" }}
        >
          <div className="v-surface" style={{ padding: 20, display: "flex", alignItems: "center", justifyContent: "space-between", gap: 24 }}>
            <div>
              <div className="overline">
                Variations · {(vs.variations || []).length} compared
                {vs.status !== "complete" ? ` · ${vs.status}` : ""}
              </div>
              <h3 style={{ marginTop: 6, fontSize: 22 }}>{vs.title}</h3>
              <div className="mono" style={{ color: "var(--v-text-2)", fontSize: 12, marginTop: 4 }}>
                by {vs.designer_name} · {new Date(vs.created_at).toLocaleString()}
              </div>
            </div>
            <div style={{ textAlign: "right" }}>
              {vs.status === "complete" && vs.comparison?.recommended_variation_id ? (
                <>
                  <div className="overline" style={{ color: "var(--v-text-2)" }}>Recommended</div>
                  <div style={{ fontSize: 22, fontWeight: 700, marginTop: 4 }}>
                    {(vs.variations || []).find((v) => v.variation_id === vs.comparison.recommended_variation_id)?.name || "—"}
                  </div>
                </>
              ) : (
                <div className="overline">{vs.status === "failed" ? "Failed" : "Processing…"}</div>
              )}
            </div>
          </div>
        </Link>
      ))}
    </div>
  );
}

function KV({ k, v }) {
  return (
    <div style={{ marginTop: 12 }}>
      <div className="overline" style={{ fontSize: 10 }}>{k}</div>
      <div style={{ marginTop: 4, fontSize: 14, lineHeight: 1.55, color: "var(--v-text)" }}>{v}</div>
    </div>
  );
}
